import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback } from 'react'

import type { RenderFunction } from 'components/uiKit/KitTypes'
import { NOOP } from 'constants/commonConstans'
import { testProps } from 'utils/test/qaData'

import DefaultHeader from '../DefaultHeader'
import * as s from './CollapseControlled.module.scss'

interface IHeaderProps {
  open: boolean
  toggle?: () => void
}

const EXIT = { height: 0, opacity: 0 }
const INITIAL = { height: 0, opacity: 0, overflow: 'visible' }

export interface IControlledCollapseProps {
  children: React.ReactNode
  header: RenderFunction<IHeaderProps> | React.ReactElement | string
  iconPosition?: 'left' | 'right' | 'none'
  styleType?: 'default' | 'ghost'
  onChangeOpen?: (val: boolean) => void
  open: boolean
  name: string
  noBorder?: boolean
  paddings?: {
    title?: string
    content?: string
    iconGap?: number
  }
  collapsible?: 'full' | 'icon'
}

const CollapseControlled: React.FC<IControlledCollapseProps> = ({
  iconPosition = 'left',
  name,
  header,
  children,
  styleType = 'default',
  open,
  onChangeOpen,
  noBorder = false,
  paddings,
  collapsible = 'full',
}) => {
  const toggle = useCallback(
    (val?: boolean) => {
      const newOpen = val ?? !open
      onChangeOpen?.(newOpen)
    },
    [onChangeOpen, open],
  )

  const animate = {
    transition: { type: 'tween' },
    height: open ? 'auto' : 0,
    opacity: open ? 1 : 0.5,
    overflow: 'hidden',
  }

  const titleElement =
    typeof header === 'function' ? (
      header({ open, toggle })
    ) : (
      <DefaultHeader
        collapsible={collapsible}
        gap={paddings?.iconGap}
        iconPosition={iconPosition}
        open={open}
        toggle={toggle}
      >
        {header}
      </DefaultHeader>
    )

  const handleLabelClick = useCallback(
    () => (collapsible === 'full' ? toggle() : NOOP),
    [toggle, collapsible],
  )

  return (
    <div className={cn(s.root, s[styleType], { [s.border]: !noBorder })}>
      <div
        {...testProps({ el: 'collapse', name, label: header, open })}
        className={cn(s.title)}
        onClick={handleLabelClick}
        style={{ padding: paddings?.title }}
      >
        {titleElement}
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            animate={animate}
            exit={EXIT}
            initial={INITIAL}
            style={{ overflow: 'hidden' }}
          >
            <div className={s.contentWrapper} style={{ padding: paddings?.content }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default CollapseControlled
